import { ReceptionBellMoneyIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import React from "react";
import {
    Audit,
    LineItemObject,
    LineItemType,
    Payment,
    PaymentLineItem,
    PaymentMethod,
    Purchase,
    PurchaseItem,
} from "types";
import { useSeller } from "../../../../hooks/useAccount";
import { getPaymentMethodName } from "../../../../models/Payment";
import { PaymentMethodIcon } from "../../../Common/PaymentMethodIcon";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";

interface AddChargeTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    lineItem: PaymentLineItem;
    change: any;
    showDate?: boolean;
}

export const PartnerTimelineItem = ({
    purchase,
    item,
    audit,
    lineItem,
    change,
    showDate = false,
}: AddChargeTimelineItemProps) => {
    const seller = useSeller();

    if (!PartnerTimelineItem.supportsAudit(purchase, item, audit, lineItem, "", change)) {
        return null;
    }

    const isCorrectLineItemExists = isSomeLineItemPassed(item);
    const partnerLineItemFromChange = isCorrectLineItemExists ? lineItem : getPartnerLineItemFromChange(change);
    const [payment, reason] = getAmount(partnerLineItemFromChange);

    let auditItem = partnerLineItemFromChange ?? lineItem;
    if (!auditItem.createdBy) {
        auditItem = { ...auditItem, createdBy: purchase.createdBy };
    }

    const { method } = payment;

    const { title, color } = getTitleAndColor(partnerLineItemFromChange?.type, partnerLineItemFromChange?.object);

    return (
        <TimelineItem
            title={title}
            audit={auditItem}
            icon={<ReceptionBellMoneyIcon />}
            color={color}
            purchase={purchase}
            showDate={showDate}
        >
            <div className="space-y-2">
                <div className="flex flex-row space-x-2">
                    {method && (
                        <Badge
                            color="secondary"
                            icon={<PaymentMethodIcon size="small" method={method ?? PaymentMethod.OTHER} />}
                            className="place-self-start self-start"
                        >
                            {getPaymentMethodName(seller, method)}
                        </Badge>
                    )}
                </div>
                <TimelineItemReason>{reason}</TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

const isPartnerLineItemType = (type: LineItemType) =>
    type === LineItemType.AFFILIATE_DEPOSIT || type === LineItemType.AFFILIATE_DISCOUNT;

PartnerTimelineItem.supportsAudit = (
    _purchase: Purchase,
    item: PurchaseItem,
    _audit: Audit,
    lineItem: PaymentLineItem,
    _timezone: any,
    change: any,
) => {
    const partnerLineItemFromChange = getPartnerLineItemFromChange(change);
    const isCorrectLineItemExists = isSomeLineItemPassed(item);

    return isPartnerLineItemType(isCorrectLineItemExists ? lineItem?.type : partnerLineItemFromChange?.type);
};

const isSomeLineItemPassed = (item: PurchaseItem) =>
    item.lineItems.find((lineItem) => isPartnerLineItemType(lineItem?.type));

const getAmount = (lineItem: any): [Payment, string] => {
    if (isPartnerLineItemType(lineItem?.type) && lineItem.payment) {
        return [lineItem.payment, lineItem.name ?? "Initial charge"];
    }

    return [{ method: "" }, lineItem.name];
};

const getTitleAndColor = (type: LineItemType, object: LineItemObject) => {
    if (type === LineItemType.AFFILIATE_DEPOSIT) {
        return object === LineItemObject.REFUND
            ? { title: "Partner Removed", color: "danger" }
            : { title: "Partner Deposit", color: "primary" };
    }

    if (type === LineItemType.AFFILIATE_COMMISSION) {
        return { title: "Partner Commission", color: "primary" };
    }

    if (type === LineItemType.AFFILIATE_DISCOUNT) {
        return { title: "Partner Discount", color: "primary" };
    }

    return { title: "", color: "primary" };
};

const getPartnerLineItemFromChange = (change: any) => {
    const pastPartnerLineItem = change?.data?.lineItems ? Object.values(change?.data?.lineItems) : null;
    const lastItem: any = Array.isArray(pastPartnerLineItem)
        ? pastPartnerLineItem.find((object: any) => object?.new || object?.old)
        : null;
    const existedPartnerLineItem = lastItem?.old ?? lastItem?.new;

    return existedPartnerLineItem;
};
